import { useEffect, useState } from "react";
import { useRecentSamples } from "../api";
import { useUserLocations } from "../api/user";
import Footer from "../components/Footer";
import Navbar from "../components/SpectraNavbar";
import { CardA } from "../components/ui/Cards";
import { SequenceD } from "../components/ui/Sequence";
import { DateTime } from "luxon";
import { useSampleContext } from "../contexts";
import SampleCons from "../constants/sample";
import DownloadData from "../components/DownloadData";

export default function PortableSamples() {
  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);

  const { getLabel } = useSampleContext();

  const [dateRange, setDateRange] = useState({
    start: sevenDaysAgo,
    end: today,
  });
  const [selectedLocation, setSelectedLocation] = useState("All");

  const { data: locations, isLoading: isLocationsLoading } = useUserLocations();

  const handleDateRangeChange = (event) => {
    const selectedDateRange = parseInt(event.target.value, 10);
    let endDate = new Date(); // Current date
    let startDate = new Date();
    startDate.setDate(endDate.getDate() - selectedDateRange);
    setDateRange({ start: startDate, end: endDate });
  };

  const handleLocationChange = (event) => {
    setSelectedLocation(event.target.value);
  };

  const {
    data: recentSamples,
    isLoading,
    isSuccess,
    isError,
  } = useRecentSamples(dateRange, selectedLocation, "portable");

  let tableData =
    isLoading || isError || isLocationsLoading
      ? []
      : recentSamples.sequences.map((sample) => {
          let newItem = {};

          let localDate = DateTime.fromISO(sample.processedDate).setZone(
            "local"
          );

          newItem.processedDate = localDate.toFormat("DDD");

          newItem.time = localDate.toFormat("t");

          newItem.foodMatrix =
            getLabel(sample.foodMatrix, SampleCons.FOOD_MATRIX) ||
            sample.foodMatrix;
          newItem.analyte =
            getLabel(sample.analyte, SampleCons.ANALYTE) || sample.analyte;
          newItem.extractionSolvent =
            getLabel(sample.extractionSolvent, SampleCons.EXTRACTION_SOLVENT) ||
            sample.extractionSolvent;

          newItem.bulkScanFindings =
            sample.tests.filter(
              (item) =>
                item.rb_findings?.length > 0 &&
                item.scanType.toLowerCase() === "bulk-burst"
            ).length > 0
              ? sample.tests
                  .filter(
                    (item) =>
                      item.rb_findings.length > 0 &&
                      item.scanType.toLowerCase() === "bulk-burst"
                  )
                  .map(
                    (item) =>
                      item.rb_findings.map((finding) => finding.substance)
                    // .join(", ")
                  )
                  .flat()
              : sample.tests.filter(
                  (item) =>
                    item.findings?.length > 0 &&
                    item.scanType.toLowerCase() === "bulk-burst"
                ).length > 0
              ? sample.tests
                  .filter(
                    (item) =>
                      item.findings?.length > 0 &&
                      item.scanType.toLowerCase() === "bulk-burst"
                  )
                  .map(
                    (item) => item.findings.map((finding) => finding.substance)
                    // .join(", ")
                  )
                  .flat()
              : [];

          if (
            newItem.bulkScanFindings.includes(
              sample.foodMatrix || sample.extractionSolvent
            )
          ) {
            newItem.bulkScanFindings = newItem.bulkScanFindings.filter(
              (finding) =>
                finding !== sample.foodMatrix &&
                finding !== sample.extractionSolvent
            );
            if (newItem.bulkScanFindings.length === 0) {
              newItem.bulkScanFindings.push("Inconclusive");
            }
          }

          newItem.bulkScanExport = newItem.bulkScanFindings.join(", ");

          newItem.traceScanFindings =
            sample.tests.filter(
              (item) =>
                item.rb_findings?.length > 0 &&
                item.scanType.toLowerCase() === "trace-burst"
            ).length > 0
              ? sample.tests
                  .filter(
                    (item) =>
                      item.rb_findings.length > 0 &&
                      item.scanType.toLowerCase() === "trace-burst"
                  )
                  .map(
                    (item) =>
                      item.rb_findings.map((finding) => finding.substance)
                    // .join(", ")
                  )
                  .flat()
              : sample.tests.filter(
                  (item) =>
                    item.findings?.length > 0 &&
                    item.scanType.toLowerCase() === "trace-burst"
                ).length > 0
              ? sample.tests
                  .filter(
                    (item) =>
                      item.findings?.length > 0 &&
                      item.scanType.toLowerCase() === "trace-burst"
                  )
                  .map((item) =>
                    item.findings.map((finding) => finding.substance)
                  )
                  .flat()
              : [];

          if (
            newItem.traceScanFindings.includes(
              sample.foodMatrix || sample.extractionSolvent
            )
          ) {
            newItem.traceScanFindings = newItem.traceScanFindings.filter(
              (finding) =>
                finding !== sample.foodMatrix &&
                finding !== sample.extractionSolvent
            );
            if (newItem.traceScanFindings.length === 0) {
              newItem.traceScanFindings.push("Inconclusive");
            }
          }

          newItem.traceScanExport = newItem.traceScanFindings.join(", ");

          newItem._id = sample._id;
          newItem.sampleId = sample.sampleId;
          newItem.location = locations.find(
            (location) => location.locationId === sample.locationId
          ).name;

          return newItem;
        });

  useEffect(() => {
    if (locations && locations.length > 1) {
      setSelectedLocation("All");
    } else if (locations && locations.length === 1) {
      setSelectedLocation(locations[0].locationId);
    }
  }, [locations]);

  const calculateSelectedValue = () => {
    // Calculate the number of days in the date range
    //NOTE: When there is a time change in the date range, the calculation will be off by an hour,so we round it to the nearest whole number
    const daysDifference = Math.round(
      (dateRange.end - dateRange.start) / (1000 * 60 * 60 * 24)
    );

    // Find the corresponding option value
    switch (daysDifference) {
      case 7:
        return 7;
      case 14:
        return 14;
      case 30:
        return 30;
      case 60:
        return 60;
      case 90:
        return 90;
      default:
        return 0; // Default to 0 if it doesn't match any option
    }
  };

  if (isLoading) {
    return (
      <div className='flex-container'>
        <Navbar signedIn={false} />
        <div
          className='px-sm-3 px-lg-5 py-3 flex-body'
          style={{ marginTop: "79px" }}
        >
          <div
            className='alert  mx-3'
            role='alert'
            style={{ backgroundColor: "#7ECBF7", fontWeight: "bold" }}
          >
            Fetching samples ...
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex-container'>
        <Navbar signedIn={false} />
        <div
          className='px-sm-3 px-lg-5 py-3 flex-body'
          style={{ marginTop: "79px" }}
        >
          <div className='alert alert-danger mx-3' role='alert'>
            Something went wrong, try refreshing the page
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className='flex-container'>
        <Navbar signedIn={false} />
        <div
          className='px-sm-3 px-lg-5 py-3 flex-body'
          style={{ marginTop: "79px" }}
        >
          {isSuccess && !isLocationsLoading && (
            <CardA
              title='Portable Raman Samples'
              subtitle='Click on a row to view and edit the sample.'
            >
              <div className='d-flex mb-3 justify-content-between '>
                <div className='d-flex'>
                  <select
                    className='form-select w-auto'
                    id='dateRangeSelect'
                    value={calculateSelectedValue()}
                    onChange={handleDateRangeChange}
                  >
                    <option value={7}>Past 7 Days</option>
                    <option value={14}>Past 14 Days</option>
                    <option value={30}>Past 30 Days</option>
                    <option value={60}>Past 60 Days</option>
                    <option value={90}>Past 90 Days</option>
                  </select>

                  {locations.length > 1 && (
                    <select
                      className='form-select w-auto'
                      id='locationSelect'
                      value={selectedLocation}
                      onChange={handleLocationChange}
                      style={{ marginLeft: "0.5rem" }}
                    >
                      <option key={"All"} value={"All"}>
                        All Locations
                      </option>
                      {locations.map((location) => (
                        <option
                          key={location.locationId}
                          value={location.locationId}
                        >
                          {location.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <DownloadData
                  samples={recentSamples.sequences}
                  needsScanData={true}
                />
              </div>
              {tableData.length === 0 ? (
                <div className='d-flex justify-content-center align-items-center flex-column flex-grow-1'>
                  <i className='bi bi-search fs-1'></i>
                  <h5>No samples found</h5>
                  <h6>Please adjust the filter settings.</h6>
                </div>
              ) : (
                <div className='table-responsive flex-grow-1'>
                  <table className='table table-hover'>
                    <thead>
                      <tr className='text-center'>
                        <th scope='col' className='p-1'>
                          Sample ID
                        </th>
                        <th scope='col' className='p-1'>
                          Date
                        </th>
                        <th scope='col' className='p-1'>
                          Time
                        </th>
                        <th scope='col' className='p-1'>
                          Food Matrix
                        </th>
                        <th scope='col' className='p-1'>
                          Analyte
                        </th>
                        <th scope='col' className='p-1'>
                          Extraction Solvent
                        </th>
                        <th scope='col' className='p-1'>
                          Raman Results
                        </th>
                        <th scope='col' className='p-1'>
                          SERS Results
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((sample) => (
                        <tr
                          key={sample._id}
                          onClick={() =>
                            (window.location = `/sample/${sample._id}`)
                          }
                          style={{ cursor: "pointer" }}
                        >
                          <td className='text-center align-middle'>
                            {sample.sampleId}
                          </td>
                          <td className='text-center align-middle'>
                            {sample.processedDate}
                          </td>
                          <td className='text-center align-middle'>
                            {sample.time}
                          </td>
                          <td className='text-center align-middle'>
                            {sample.foodMatrix}
                          </td>
                          <td className='text-center align-middle'>
                            {sample.analyte}
                          </td>
                          <td className='text-center align-middle'>
                            {sample.extractionSolvent}
                          </td>
                          <td className='align-middle'>
                            {
                              <SequenceD
                                findings={
                                  sample.bulkScanFindings || ["Inconclusive"]
                                }
                              />
                            }
                          </td>
                          <td className='align-middle'>
                            {
                              <SequenceD
                                findings={
                                  sample.traceScanFindings || ["Inconclusive"]
                                }
                              />
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardA>
          )}
        </div>
        <Footer />
      </div>
    );
  }
}
